/**
 * Супер-админка портала (ТЗ 6.29, ЛЦ-09…ЛЦ-13): команда портала с ролями, люди всех компаний, вход в любую
 * компанию, активность и безопасность входа, объявления всем компаниям.
 *
 * Владелец портала — всё, в том числе тарифы, лицензии, счета, продажи, Pilot и суммы. Администратор портала —
 * компании, люди и доступы, вход в компании без сумм, безопасность и объявления.
 */
import { z } from 'zod';
import { LICENSE_KINDS, LICENSE_STATES, LimitUsage, PlatformEvent } from './licensing';
import { ALL_PERMISSIONS, type PermissionCode } from './permissions';

/* ───────────────────────── Команда портала (ЛЦ-09) ───────────────────────── */

export const PLATFORM_ROLES = ['owner', 'admin'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];
export const PlatformRoleSchema = z.enum(PLATFORM_ROLES);

export const PLATFORM_ROLE_TITLES: Record<PlatformRole, string> = {
  owner: 'Владелец портала',
  admin: 'Администратор портала',
};

export const PLATFORM_ROLE_HINTS: Record<PlatformRole, string> = {
  owner:
    'Всё: компании, люди, вход в компании с суммами, тарифы, лицензии, счета, продажи, Pilot, команда портала',
  admin: 'Компании, люди и доступы всех компаний, вход в компании без сумм, безопасность входа и объявления',
};

/** Что открыто в кабинете: раздел кабинета или действие */
export const PLATFORM_ABILITIES = [
  'companies',
  'users',
  'entry',
  'security',
  'announcements',
  'owner_transfer',
  'team',
  'billing',
] as const;
export type PlatformAbility = (typeof PLATFORM_ABILITIES)[number];

const ROLE_ABILITIES: Record<PlatformRole, readonly PlatformAbility[]> = {
  owner: PLATFORM_ABILITIES,
  admin: ['companies', 'users', 'entry', 'security', 'announcements'],
};

export function platformCan(role: PlatformRole | null | undefined, ability: PlatformAbility): boolean {
  return Boolean(role && ROLE_ABILITIES[role].includes(ability));
}

export const PlatformTeamMember = z.object({
  userId: z.uuid(),
  fullName: z.string(),
  email: z.string(),
  role: PlatformRoleSchema,
  isActive: z.boolean(),
  lastLoginAt: z.string().nullable(),
  /** С какого времени в команде; у владельца, назначенного при установке, — null */
  since: z.string().nullable(),
  addedByName: z.string().nullable(),
  note: z.string().nullable(),
});
export type PlatformTeamMember = z.infer<typeof PlatformTeamMember>;

export const PlatformTeamInput = z.object({
  email: z.email().transform((s) => s.trim().toLowerCase()),
  role: PlatformRoleSchema,
  note: z.string().trim().max(300).nullable().optional(),
});
export type PlatformTeamInput = z.infer<typeof PlatformTeamInput>;

/** Смена роли; null — убрать из команды портала */
export const PlatformTeamPatch = z.object({ role: PlatformRoleSchema.nullable() });
export type PlatformTeamPatch = z.infer<typeof PlatformTeamPatch>;

/* ───────────────────────── Вход в компанию (ЛЦ-11) ───────────────────────── */

export const ENTRY_MODES = ['view', 'edit'] as const;
export type EntryMode = (typeof ENTRY_MODES)[number];
export const ENTRY_MODE_TITLES: Record<EntryMode, string> = { view: 'просмотр', edit: 'правка' };

/** Сколько часов действует вход; по умолчанию — 2 */
export const ENTRY_HOURS = [1, 2, 4, 8] as const;
export const ENTRY_DEFAULT_HOURS = 2;

export const ENTRY_END_REASONS = ['exit', 'expired', 'replaced', 'logout', 'revoked', 'switched'] as const;
export type EntryEndReason = (typeof ENTRY_END_REASONS)[number];
export const ENTRY_END_TITLES: Record<EntryEndReason, string> = {
  exit: 'вышел сам',
  expired: 'время вышло',
  replaced: 'вошёл в другую компанию',
  logout: 'вышел из портала',
  revoked: 'роль в команде портала снята',
  switched: 'переключился на свою компанию',
};

/** Суммы при входе: «все» — только владельцу портала, администратору портала — «без сумм» */
export const EntryMoneySchema = z.enum(['all', 'none']);
export type EntryMoney = z.infer<typeof EntryMoneySchema>;

/**
 * Права в компании при входе. Просмотр — все права «видеть» и страницы администрирования (изменения отклоняет
 * сервер, транзакции только чтение). Правка: владелец портала — все права администратора компании; администратор
 * портала — люди, роли, справочники и настройки, остальное только видит.
 */
const ENTRY_READ_EXTRA: readonly PermissionCode[] = [
  'procurement.dashboard',
  'admin.users',
  'admin.roles',
  'admin.org',
  'admin.audit',
  'admin.settings',
];
export const ENTRY_READ_PERMISSIONS: PermissionCode[] = ALL_PERMISSIONS.filter(
  (p) => p.endsWith('.view') || ENTRY_READ_EXTRA.includes(p),
);

export function entryPermissions(role: PlatformRole, mode: EntryMode): PermissionCode[] {
  return role === 'owner' && mode === 'edit' ? [...ALL_PERMISSIONS] : [...ENTRY_READ_PERMISSIONS];
}

export const PlatformEntryInput = z
  .object({
    tenantId: z.uuid(),
    mode: z.enum(ENTRY_MODES).default('view'),
    hours: z
      .number()
      .int()
      .refine((h) => (ENTRY_HOURS as readonly number[]).includes(h), 'Срок входа — 1, 2, 4 или 8 часов')
      .default(ENTRY_DEFAULT_HOURS),
    reason: z.string().trim().max(500).nullable().optional(),
    /** Показывать суммы — только владельцу портала; администратору портала суммы не открываются */
    money: z.boolean().default(true),
  })
  .refine((v) => v.mode === 'view' || (v.reason?.length ?? 0) >= 5, {
    path: ['reason'],
    message: 'Для входа с правкой напишите причину — она попадёт в журнал компании',
  });
export type PlatformEntryInput = z.infer<typeof PlatformEntryInput>;

/** Действующий вход: в /auth/me и в плашке на каждой странице */
export const PlatformEntryState = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string(),
  role: PlatformRoleSchema,
  mode: z.enum(ENTRY_MODES),
  money: EntryMoneySchema,
  reason: z.string().nullable(),
  startedAt: z.string(),
  expiresAt: z.string(),
});
export type PlatformEntryState = z.infer<typeof PlatformEntryState>;

export const PlatformEntryLogRow = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantName: z.string().nullable(),
  userId: z.uuid(),
  userName: z.string().nullable(),
  role: PlatformRoleSchema,
  mode: z.enum(ENTRY_MODES),
  money: EntryMoneySchema,
  reason: z.string().nullable(),
  startedAt: z.string(),
  expiresAt: z.string(),
  endedAt: z.string().nullable(),
  endReason: z.enum(ENTRY_END_REASONS).nullable(),
  ip: z.string().nullable(),
  device: z.string().nullable(),
});
export type PlatformEntryLogRow = z.infer<typeof PlatformEntryLogRow>;

/* ───────────────────────── Компании: активность (ЛЦ-12) ───────────────────────── */

export const ConsoleCompany = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  isDemo: z.boolean(),
  status: z.string(),
  licenseState: z.enum(LICENSE_STATES),
  daysLeft: z.number().int().nullable(),
  planName: z.string().nullable(),
  /** Активные участники: сотрудники и внешние */
  staff: z.number().int(),
  external: z.number().int(),
  projects: z.number().int(),
  /** Люди компании, работавшие в портале за 7 и 30 дней */
  active7: z.number().int(),
  active30: z.number().int(),
  lastActivityAt: z.string().nullable(),
  /** Входы и неудачные попытки людей компании за 7 дней */
  logins7: z.number().int(),
  failed7: z.number().int(),
  /** Команда портала сейчас внутри компании */
  activeEntries: z.number().int(),
});
export type ConsoleCompany = z.infer<typeof ConsoleCompany>;

export const ConsoleCompanyPerson = z.object({
  userId: z.uuid(),
  fullName: z.string(),
  email: z.string(),
  position: z.string().nullable(),
  kind: z.string(),
  externalProfile: z.string().nullable(),
  isOwner: z.boolean(),
  /** Членство в компании: active — работает, иначе отключён в компании */
  status: z.string(),
  /** Вход в портал: false — заблокирован командой портала */
  isActive: z.boolean(),
  moneyLevel: z.string(),
  roles: z.array(z.string()),
  lastSeenAt: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  lastIp: z.string().nullable(),
  device: z.string().nullable(),
});
export type ConsoleCompanyPerson = z.infer<typeof ConsoleCompanyPerson>;

export const ConsoleCompanyDetail = z.object({
  company: ConsoleCompany,
  license: z.object({
    state: z.enum(LICENSE_STATES),
    kind: z.enum(LICENSE_KINDS).nullable(),
    planName: z.string().nullable(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    daysLeft: z.number().int().nullable(),
    seats: z.number().int().nullable(),
    suspendedReason: z.string().nullable(),
  }),
  usage: z.array(LimitUsage),
  people: z.array(ConsoleCompanyPerson),
  entries: z.array(PlatformEntryLogRow),
  /** Журнал портала по компании без счетов и оплат */
  events: z.array(PlatformEvent),
});
export type ConsoleCompanyDetail = z.infer<typeof ConsoleCompanyDetail>;

/* ───────────────────────── Люди всех компаний (ЛЦ-10) ───────────────────────── */

export const ConsoleBlock = z.object({
  reason: z.string().nullable(),
  at: z.string(),
  byName: z.string().nullable(),
});
export type ConsoleBlock = z.infer<typeof ConsoleBlock>;

export const ConsoleUserCompany = z.object({
  tenantId: z.uuid(),
  tenantName: z.string(),
  isDemo: z.boolean(),
  isOwner: z.boolean(),
  kind: z.string(),
  externalProfile: z.string().nullable(),
  status: z.string(),
  moneyLevel: z.string(),
  position: z.string().nullable(),
  roles: z.array(z.object({ name: z.string(), scope: z.string() })),
  joinedAt: z.string(),
});
export type ConsoleUserCompany = z.infer<typeof ConsoleUserCompany>;

export const ConsoleUserRow = z.object({
  id: z.uuid(),
  fullName: z.string(),
  email: z.string(),
  isActive: z.boolean(),
  blocked: ConsoleBlock.nullable(),
  platformRole: PlatformRoleSchema.nullable(),
  lastLoginAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  /** Почта сейчас заблокирована после перебора пароля */
  locked: z.boolean(),
  companies: z.array(
    z.object({
      tenantId: z.uuid(),
      tenantName: z.string(),
      isOwner: z.boolean(),
      kind: z.string(),
      status: z.string(),
    }),
  ),
});
export type ConsoleUserRow = z.infer<typeof ConsoleUserRow>;

export const CONSOLE_USER_FILTERS = ['all', 'blocked', 'locked', 'idle', 'team'] as const;
export type ConsoleUserFilter = (typeof CONSOLE_USER_FILTERS)[number];
export const CONSOLE_USER_FILTER_TITLES: Record<ConsoleUserFilter, string> = {
  all: 'Все',
  blocked: 'Заблокированы',
  locked: 'Перебор пароля',
  idle: 'Не входили 30 дней',
  team: 'Команда портала',
};

export const ConsoleUserList = z.object({
  items: z.array(ConsoleUserRow),
  total: z.number().int(),
});
export type ConsoleUserList = z.infer<typeof ConsoleUserList>;

export const ConsoleSession = z.object({
  id: z.uuid(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  expiresAt: z.string(),
  ip: z.string().nullable(),
  device: z.string().nullable(),
  tenantName: z.string().nullable(),
});
export type ConsoleSession = z.infer<typeof ConsoleSession>;

/* ───────────────────────── Безопасность входа (ЛЦ-12) ───────────────────────── */

export const LOGIN_RESULTS = ['ok', 'bad_password', 'unknown', 'inactive', 'locked'] as const;
export type LoginResult = (typeof LOGIN_RESULTS)[number];
export const LOGIN_RESULT_TITLES: Record<LoginResult, string> = {
  ok: 'вход',
  bad_password: 'неверный пароль',
  unknown: 'нет такой почты',
  inactive: 'вход заблокирован',
  locked: 'отказ: перебор пароля',
};

export const LOGIN_METHODS = ['password', 'passkey', 'invite', 'setup'] as const;
export type LoginMethod = (typeof LOGIN_METHODS)[number];
export const LOGIN_METHOD_TITLES: Record<LoginMethod, string> = {
  password: 'пароль',
  passkey: 'ключ входа',
  invite: 'приглашение',
  setup: 'первая настройка',
};

export const LoginEvent = z.object({
  id: z.uuid(),
  at: z.string(),
  email: z.string(),
  userId: z.uuid().nullable(),
  userName: z.string().nullable(),
  tenantId: z.uuid().nullable(),
  tenantName: z.string().nullable(),
  result: z.enum(LOGIN_RESULTS),
  method: z.enum(LOGIN_METHODS),
  ip: z.string().nullable(),
  device: z.string().nullable(),
  newDevice: z.boolean(),
});
export type LoginEvent = z.infer<typeof LoginEvent>;

/**
 * Что блокируется при переборе: почта — попытки с чужих устройств (на своём устройстве, где человек уже входил по
 * этой почте, блокировка почты его не держит); device — почта на своём устройстве (свой счёт неудач); ip — адрес,
 * с которого перебирают много почт
 */
export const LOGIN_LOCK_KINDS = ['email', 'ip', 'device'] as const;
export type LoginLockKind = (typeof LOGIN_LOCK_KINDS)[number];

export const LOGIN_LOCK_KIND_TITLES: Record<LoginLockKind, string> = {
  email: 'почта, чужие устройства',
  ip: 'адрес',
  device: 'почта на своём устройстве',
};

/** Порог неудач за окно и сроки блокировки по ступеням (ДС-12: 15, 60 и 360 минут) */
export const LOGIN_LOCK_RULES = {
  windowMinutes: 15,
  thresholds: { email: 5, ip: 20, device: 5 } as Record<LoginLockKind, number>,
  /** Адрес, с которого за 7 дней был удачный вход (офис, общий выход в интернет), блокируется позже */
  knownIpThreshold: 100,
  minutes: [15, 60, 360] as const,
  /** Ступень растёт, если прежняя блокировка того же ключа была за последние сутки */
  escalationHours: 24,
  /** Сколько дней устройство считается своим после удачного входа с него */
  trustedDeviceDays: 90,
};

export const LoginLock = z.object({
  id: z.uuid(),
  kind: z.enum(LOGIN_LOCK_KINDS),
  key: z.string(),
  level: z.number().int(),
  failures: z.number().int(),
  lockedAt: z.string(),
  lockedUntil: z.string(),
  releasedAt: z.string().nullable(),
  releasedByName: z.string().nullable(),
  /** Почему снята, если не кнопкой команды портала (смена пароля по ссылке из письма) */
  releaseNote: z.string().nullable(),
  active: z.boolean(),
});
export type LoginLock = z.infer<typeof LoginLock>;

export const SecurityOverview = z.object({
  logins24h: z.number().int(),
  failed24h: z.number().int(),
  logins7d: z.number().int(),
  failed7d: z.number().int(),
  newDevices7d: z.number().int(),
  activeLocks: z.number().int(),
  blockedUsers: z.number().int(),
  topFailedIps: z.array(z.object({ ip: z.string(), count: z.number().int() })),
  topFailedEmails: z.array(z.object({ email: z.string(), count: z.number().int() })),
  /** Сколько дней хранится журнал входов */
  retentionDays: z.number().int(),
});
export type SecurityOverview = z.infer<typeof SecurityOverview>;

export const LOGIN_EVENT_FILTERS = ['all', 'failed', 'ok', 'new_device'] as const;
export type LoginEventFilter = (typeof LOGIN_EVENT_FILTERS)[number];

export const ConsoleUserDetail = z.object({
  user: z.object({
    id: z.uuid(),
    fullName: z.string(),
    email: z.string(),
    position: z.string().nullable(),
    locale: z.string(),
    isActive: z.boolean(),
    createdAt: z.string(),
    lastLoginAt: z.string().nullable(),
    hasPasskey: z.boolean(),
    platformRole: PlatformRoleSchema.nullable(),
  }),
  blocked: ConsoleBlock.nullable(),
  companies: z.array(ConsoleUserCompany),
  sessions: z.array(ConsoleSession),
  logins: z.array(LoginEvent),
  locks: z.array(LoginLock),
  entries: z.array(PlatformEntryLogRow),
  events: z.array(PlatformEvent),
  /** Что можно сделать с этим человеком тому, кто смотрит */
  can: z.object({
    block: z.boolean(),
    revokeSessions: z.boolean(),
    unlock: z.boolean(),
    makeOwner: z.boolean(),
    /** Отправить ссылку для нового пароля: учётная запись открыта, не общая демо-запись, почта портала подключена */
    passwordReset: z.boolean(),
  }),
});
export type ConsoleUserDetail = z.infer<typeof ConsoleUserDetail>;

export const ConsoleBlockInput = z.object({ reason: z.string().trim().min(3).max(500) });
export type ConsoleBlockInput = z.infer<typeof ConsoleBlockInput>;

/** Передать владение компанией (только владелец портала) */
export const OwnerTransferInput = z.object({
  userId: z.uuid(),
  /** Оставить прежних владельцев владельцами; по умолчанию владелец один — новый */
  keepPrevious: z.boolean().default(false),
});
export type OwnerTransferInput = z.infer<typeof OwnerTransferInput>;

/* ───────────────────────── Объявления (ЛЦ-13) ───────────────────────── */

export const ANNOUNCEMENT_AUDIENCES = ['admins', 'staff'] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];
export const ANNOUNCEMENT_AUDIENCE_TITLES: Record<AnnouncementAudience, string> = {
  admins: 'Администраторам компаний',
  staff: 'Всем сотрудникам',
};

export const ANNOUNCEMENT_LEVELS = ['info', 'important'] as const;
export type AnnouncementLevel = (typeof ANNOUNCEMENT_LEVELS)[number];
export const ANNOUNCEMENT_LEVEL_TITLES: Record<AnnouncementLevel, string> = {
  info: 'Обычное',
  important: 'Важное',
};

export const ANNOUNCEMENT_STATES = ['scheduled', 'active', 'ended', 'cancelled'] as const;
export type AnnouncementState = (typeof ANNOUNCEMENT_STATES)[number];
export const ANNOUNCEMENT_STATE_TITLES: Record<AnnouncementState, string> = {
  scheduled: 'запланировано',
  active: 'показывается',
  ended: 'закончилось',
  cancelled: 'отменено',
};

export const AnnouncementInput = z
  .object({
    title: z.string().trim().min(3).max(120),
    body: z.string().trim().min(3).max(2000),
    level: z.enum(ANNOUNCEMENT_LEVELS).default('info'),
    audience: z.enum(ANNOUNCEMENT_AUDIENCES),
    /** null — всем компаниям (кроме демо), иначе выбранным */
    tenantIds: z.array(z.uuid()).min(1).max(500).nullable(),
    /** Начало показа; null — сразу */
    startsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    endsAt: z.iso.datetime({ offset: true }),
    /** Отправить письмом и в Telegram по настройкам уведомлений людей */
    notify: z.boolean().default(false),
  })
  .refine((v) => Date.parse(v.endsAt) > (v.startsAt ? Date.parse(v.startsAt) : Date.now()), {
    path: ['endsAt'],
    message: 'Окончание показа — позже начала',
  });
export type AnnouncementInput = z.infer<typeof AnnouncementInput>;

export const Announcement = z.object({
  id: z.uuid(),
  title: z.string(),
  body: z.string(),
  level: z.enum(ANNOUNCEMENT_LEVELS),
  audience: z.enum(ANNOUNCEMENT_AUDIENCES),
  tenantIds: z.array(z.uuid()).nullable(),
  tenantNames: z.array(z.string()),
  startsAt: z.string(),
  endsAt: z.string(),
  notify: z.boolean(),
  /** Сколько людей видят объявление по составу компаний на момент запроса */
  recipients: z.number().int(),
  /** Сообщений поставлено в очередь на почту и в Telegram */
  queued: z.number().int(),
  notifiedAt: z.string().nullable(),
  dismissed: z.number().int(),
  state: z.enum(ANNOUNCEMENT_STATES),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  cancelledAt: z.string().nullable(),
});
export type Announcement = z.infer<typeof Announcement>;

/** Объявление в портале у человека: плашка с «Скрыть» */
export const MyAnnouncement = z.object({
  id: z.uuid(),
  title: z.string(),
  body: z.string(),
  level: z.enum(ANNOUNCEMENT_LEVELS),
  startsAt: z.string(),
  endsAt: z.string(),
});
export type MyAnnouncement = z.infer<typeof MyAnnouncement>;

/** Сколько получат объявление, до отправки */
export const AnnouncementPreview = z.object({
  companies: z.number().int(),
  recipients: z.number().int(),
});
export type AnnouncementPreview = z.infer<typeof AnnouncementPreview>;

/* ───────────────────────── Сводка кабинета для команды портала ───────────────────────── */

export const ConsoleOverview = z.object({
  companies: z.number().int(),
  people: z.number().int(),
  active7: z.number().int(),
  failed24h: z.number().int(),
  activeLocks: z.number().int(),
  blockedUsers: z.number().int(),
  activeEntries: z.number().int(),
  activeAnnouncements: z.number().int(),
});
export type ConsoleOverview = z.infer<typeof ConsoleOverview>;

/** Простое название устройства по строке браузера: «Chrome · Windows» */
export function deviceOf(userAgent: string | null | undefined): string | null {
  const ua = userAgent ?? '';
  if (!ua) return null;
  const browser = /YaBrowser\//.test(ua)
    ? 'Яндекс Браузер'
    : /Edg(e|A|iOS)?\//.test(ua)
      ? 'Edge'
      : /OPR\/|Opera/.test(ua)
        ? 'Opera'
        : /Firefox\/|FxiOS\//.test(ua)
          ? 'Firefox'
          : /Chrome\/|CriOS\//.test(ua)
            ? 'Chrome'
            : /Safari\//.test(ua)
              ? 'Safari'
              : /curl|python|node|axios|Go-http/i.test(ua)
                ? 'программа'
                : 'браузер';
  const os = /iPhone|iPad|iPod/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Mac OS X|Macintosh/.test(ua)
          ? 'macOS'
          : /Linux|X11/.test(ua)
            ? 'Linux'
            : null;
  return os ? `${browser} · ${os}` : browser;
}
